import { getConfig } from "lib/config";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NodeJournalApi } from "~/api/node-journal-api";
import { CustomFileInput } from "~/components/common/file-input";
import { Button } from "~/components/ui";

export default function InstallationImageFormField({
  value,
  onChange,
  deveui,
}: {
  value: string | File;
  onChange: (val: File | null) => void;
  deveui: string;
}) {
  const { t } = useTranslation();
  const baseUrl = getConfig().BASE_URL;
  const [localValue, setLocalValue] = useState<string>(
    value instanceof File ? value.name : value,
  );
  const [src, setSrc] = useState<string>(
    value instanceof File ? "" : value ? `${baseUrl}/image/${value}` : "",
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileSrc, setImageFileSrc] = useState<string>("");

  useEffect(() => {
    if (!imageFile) {
      setImageFileSrc("");
      return;
    }

    const url = URL.createObjectURL(imageFile);
    setImageFileSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  async function handleImageSelect(file: File) {
    setLocalValue(file.name);
    setImageFile(file);
    setSrc("");
    onChange(file);
  }

  async function handleImageDelete() {
    const result = await new NodeJournalApi().deleteImage(deveui);
    if (!result.ok) {
      toast.error(t("common.errors.deleteFailed"));
      console.error(result.error);
      return;
    }
    toast.success(
      t("common.deleted", { resource: t("tag.fields.installationImage") }),
    );
    setSrc("");
    setImageFile(null);
    setLocalValue("");
    onChange(null);
  }

  return (
    <>
      <CustomFileInput
        value={localValue}
        placeholder={t("tag.placeholders.installationImage")}
        onChange={handleImageSelect}
        accept="image/*"
        className="w-fit dark:bg-input/30 border-input h-9 rounded-md border bg-background px-3 py-1 mb-1 text-base shadow-xs outline-none md:text-sm"
      />
      {value && src ? (
        <img src={src} alt="image" />
      ) : imageFileSrc ? (
        <img src={imageFileSrc} alt="Uploaded" />
      ) : (
        <></>
      )}
      <Button
        className="w-fit text-red-500 border-red-500"
        onClick={handleImageDelete}
        variant={"outline"}
        aria-label="Delete Image"
        disabled={localValue === ""}
        type="button"
      >
        {t("common.delete")}
      </Button>
    </>
  );
}
